import { Button, Chip, Input, Textarea } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Hand } from 'lucide-react'
import * as React from 'react'
import { settlePending, useTrackedDefault } from '../time/tracked'
import { trackedKeys } from '../time/tracked-api'
import { cardLinksQuery } from './api'
import { LinksState } from './links-list'

/**
 * A `do_it_yourself` card's body (issue #274): the path it took ("You'll need to"), why Riley can't do it, the exact
 * steps, the links that task needs (from the records we hold, with what is missing) and the Mark done form. Marking
 * it done confirms the card with the minutes and outcome the person entered; Patchbay logs the time under their own
 * Desk agent and notes the outcome on the case.
 */
export const DIY_KIND = 'do_it_yourself'

const WHY: Record<string, string> = {
  no_api: 'Riley has no API or card for this step',
  no_permission: "Riley's credentials are not allowed to do this",
  needs_onsite: 'This needs someone on site',
  needs_judgement: "This needs a person's judgement",
}

export interface DiyPayload {
  title?: string
  steps?: string[]
  why?: string
  why_detail?: string
  links?: string[]
  minutes_estimate?: number
  trivial?: boolean
}

export function diyPayload(payload: Record<string, unknown>): DiyPayload {
  const steps = Array.isArray(payload.steps) ? payload.steps.filter((s): s is string => typeof s === 'string') : []
  return {
    title: typeof payload.title === 'string' ? payload.title : undefined,
    steps,
    why: typeof payload.why === 'string' ? payload.why : undefined,
    why_detail: typeof payload.why_detail === 'string' ? payload.why_detail : undefined,
    minutes_estimate: typeof payload.minutes_estimate === 'number' ? payload.minutes_estimate : undefined,
    trivial: payload.trivial === true,
  }
}

export function whySentence(p: DiyPayload): string {
  return p.why_detail?.trim() || (p.why ? (WHY[p.why] ?? p.why) : 'Riley cannot do this step itself')
}

/** "Riley can do this" or "You'll need to": the path a card took (issue #274). Null for cards a person proposed. */
export function cardPath(card: { kind: string; riley_card_id?: string | null }): 'riley' | 'you' | null {
  if (card.kind === DIY_KIND) return 'you'
  if (card.riley_card_id || card.kind === 'riley_action') return 'riley'
  return null
}

export function CardPathChip({ card }: { card: { kind: string; riley_card_id?: string | null } }) {
  const path = cardPath(card)
  if (!path) return null
  return path === 'you' ? (
    <Chip tone="warning" size="sm" data-slot="card-path">
      <Hand /> You'll need to
    </Chip>
  ) : (
    <Chip tone="success" size="sm" data-slot="card-path">
      Riley can do this · one click
    </Chip>
  )
}

export function DiyBody({ payload, caseId, cardId }: { payload: Record<string, unknown>; caseId?: string | null; cardId: string }) {
  const p = diyPayload(payload)
  const links = useQuery({ ...cardLinksQuery(caseId ?? '', cardId), enabled: Boolean(caseId) })
  return (
    <div className="mt-2 space-y-2.5 text-sm" data-slot="diy">
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground/85">Why Riley can't:</span> {whySentence(p)}
        {p.minutes_estimate ? <span className="text-subtle"> · about {p.minutes_estimate} min</span> : null}
      </p>
      {p.steps?.length ? (
        <ol className="list-decimal space-y-1 rounded-md bg-muted/60 py-2 pr-3 pl-7" aria-label="Steps">
          {p.steps.map((step, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: steps are a frozen ordered list and may repeat.
            <li key={i}>{step}</li>
          ))}
        </ol>
      ) : null}
      {caseId ? (
        <section className="rounded-md border border-border px-2 py-2" aria-label="Links for this task">
          <LinksState q={links} />
        </section>
      ) : null}
    </div>
  )
}

/** Mark done: minutes (optional) and what happened, sent as the card's edited payload. */
export function DiyDoneForm({
  caseId,
  trivial = false,
  busy,
  onDone,
}: {
  caseId?: string | null
  trivial?: boolean
  busy: boolean
  onDone: (edit: { minutes: number; outcome: string; tracked_seconds?: number }) => boolean | Promise<boolean>
}) {
  const queryClient = useQueryClient()
  const tracked = useTrackedDefault(caseId)
  const touchedMinutes = React.useRef(false)
  const [minutes, setMinutes] = React.useState('')
  const [showTime, setShowTime] = React.useState(!trivial)
  const [outcome, setOutcome] = React.useState('')
  React.useEffect(() => {
    if (!touchedMinutes.current && tracked.minutes > 0) setMinutes(String(tracked.minutes))
  }, [tracked.minutes])
  const parsed = minutes.trim() === '' ? 0 : Number(minutes)
  const valid = Number.isInteger(parsed) && parsed >= 0 && parsed <= 1440
  const submittedMinutes = showTime ? parsed : 0
  return (
    <form
      className="mt-3 space-y-2"
      data-slot="diy-done"
      onSubmit={(event) => {
        event.preventDefault()
        if (valid && !busy) {
          void (async () => {
            const completed = await onDone({
              minutes: submittedMinutes,
              outcome: outcome.trim(),
              ...(submittedMinutes > 0 && tracked.seconds > 0 ? { tracked_seconds: tracked.seconds } : {}),
            })
            if (completed !== false && caseId) {
              settlePending(caseId)
              await queryClient.invalidateQueries({ queryKey: trackedKeys.case(caseId) })
              await queryClient.invalidateQueries({ queryKey: trackedKeys.unlogged })
            }
          })()
        }
      }}
    >
      <label className="block space-y-1 text-xs font-medium">
        What happened
        <Textarea
          value={outcome}
          maxLength={2000}
          onChange={(event) => setOutcome(event.target.value)}
          placeholder="e.g. Port 7 cycled; the AP came back online"
          className="min-h-16"
        />
      </label>
      <div className="flex items-end gap-2">
        {showTime ? (
          <label className="block space-y-1 text-xs font-medium" data-slot="diy-time">
            Minutes
            <Input
              inputMode="numeric"
              value={minutes}
              onChange={(event) => {
                touchedMinutes.current = true
                setMinutes(event.target.value)
              }}
              className="w-24"
              aria-invalid={!valid}
              placeholder="Optional"
            />
          </label>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowTime(true)}>
            + log time
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={busy || !valid}>
          Mark done
        </Button>
      </div>
      {showTime ? (
        <>
          {tracked.error ? (
            <p role="status" className="text-2xs text-warning">
              Tracked time is unreachable: {tracked.error} Enter minutes yourself; zero has not been assumed.
            </p>
          ) : null}
          <p className="text-2xs text-subtle">
            {tracked.minutes > 0 ? `${tracked.source}. ` : ''}Minutes are logged on Desk under your own Desk agent; empty or 0 logs no time.
          </p>
        </>
      ) : null}
    </form>
  )
}
